import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Calendar, Clock, Video } from "lucide-react";
import CtaButton from "./CtaButton";
import RegisterButton from "./RegisterButton";

const POSTER_URL =
    "https://customer-assets-v7afamib.emergentagent.net/job_engulf-seminar-kl/artifacts/b58f9a660a95a44c_image.png";

const line = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: {
            duration: 1,
            delay: 0.2 + i * 0.14,
            ease: [0.22, 1, 0.36, 1],
        },
    }),
};

const DETAILS = [
    { icon: Calendar, label: "JADUAL", value: "Every Monday & Tuesday", sub: "Sesi mingguan" },
    { icon: Clock, label: "MASA", value: "8:30 PM – 10:00 PM", sub: "Waktu Malaysia" },
    { icon: Video, label: "PLATFORM", value: "Live via Zoom", sub: "Sesi online" },
];

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yOrb = useTransform(scrollYProgress, [0, 1], [0, -70]);
    const yPoster = useTransform(scrollYProgress, [0, 1], [0, 90]);

    return (
        <section
            ref={ref}
            id="top"
            data-testid="hero-section"
            className="relative overflow-hidden pt-28 md:pt-36"
        >
            <motion.div
                style={{ y: yOrb }}
                className="pointer-events-none absolute -left-44 top-24 h-[480px] w-[480px] rounded-full bg-electric/25 blur-3xl"
            />
            <motion.div
                style={{ y: yPoster }}
                className="pointer-events-none absolute -right-44 top-4 h-[520px] w-[520px] rounded-full bg-neon/20 blur-3xl"
            />

            <div className="relative mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-[1fr_0.9fr] lg:items-center lg:gap-10 lg:px-8">
                <div>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.05 }}
                        className="flex flex-wrap items-center gap-3"
                    >
                        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60">
                            Queen of Dollars present
                        </p>
                        <span className="rounded-full border border-neon/50 bg-neon/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-lav">
                            Featuring HFM
                        </span>
                    </motion.div>

                    <h1
                        data-testid="hero-headline"
                        className="mt-6 font-display text-[clamp(2.6rem,7.5vw,5.25rem)] font-extrabold leading-[0.95] tracking-tight"
                    >
                        {[
                            <span key="a" className="text-chrome-grad">ENGULFING</span>,
                            <span key="b" className="text-duo-grad">NEWBIE</span>,
                            <span key="c" className="block pt-2 font-display text-[clamp(1.1rem,2.6vw,1.9rem)] font-extrabold uppercase tracking-[0.14em] text-white/85">
                                Onboard Webinar
                            </span>,
                        ].map((l, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    custom={i}
                                    variants={line}
                                    initial="hidden"
                                    animate="show"
                                    className="block"
                                >
                                    {l}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75 }}
                        className="mt-7"
                    >
                        <p className="max-w-xl leading-relaxed text-white/65">
                            Baru nak belajar teknik Engulfing? Jom kenali asas
                            candlestick, cara membaca pergerakan market dan
                            bagaimana membina kefahaman trading melalui sesi
                            pembelajaran bersama Queen of Dollars.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center"
                    >
                        <CtaButton location="Hero" testId="hero-cta-button">
                            PM Admin to Join
                        </CtaButton>
                        <RegisterButton testId="hero-register-button" />
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.35 }}
                    style={{ y: yPoster }}
                    className="group relative lg:ml-auto lg:w-full lg:max-w-[480px]"
                    data-testid="hero-poster-frame"
                >
                    <div
                        className="absolute -inset-6 rounded-[2.5rem] bg-electric/25 blur-3xl"
                        aria-hidden="true"
                    />
                    <div className="relative overflow-hidden rounded-[2rem] border border-neon/40 bg-black/60 p-2 shadow-[0_40px_120px_-40px_rgba(160,32,240,0.5)] transition-transform duration-500 md:rotate-[1.5deg] md:group-hover:rotate-0">
                        <img
                            src={POSTER_URL}
                            alt="Poster rasmi QOD Engulfing Newbie Onboard Webinar — setiap Isnin & Selasa, 8:30 malam hingga 10:00 malam, live di Zoom bersama Queen of Dollars dan HFM"
                            data-testid="hero-poster"
                            className="w-full rounded-3xl"
                        />
                    </div>
                </motion.div>
            </div>

            <div className="relative mt-16 border-t border-white/5 bg-white/[0.02] py-8 md:mt-20">
                <div className="mx-auto grid max-w-7xl grid-cols-1 gap-3 px-4 sm:grid-cols-3 sm:px-6 lg:px-8">
                    {DETAILS.map((d, i) => (
                        <motion.div
                            key={d.label}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.6, delay: i * 0.08 }}
                            data-testid={`hero-detail-${i + 1}`}
                            className="glass rounded-2xl p-4"
                        >
                            <d.icon size={18} className="text-neon" />
                            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
                                {d.label}
                            </p>
                            <p className="mt-1 font-display text-sm font-bold text-white md:text-base">
                                {d.value}
                            </p>
                            <p className="mt-0.5 text-xs text-white/40">{d.sub}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Hero;
